import type { components } from "@/api/types";

type ActivityFilter = components["schemas"]["ActivityFilter"];
type TransactionType = components["schemas"]["TransactionType"];
type MarketFilter = components["schemas"]["MarketFilter"];
type ChartPeriod = components["schemas"]["ChartPeriod"];
type ShowcasePeriod = components["schemas"]["ShowcasePeriod"];
type EarningsPeriod = components["schemas"]["EarningsPeriod"];
type RatingMode = components["schemas"]["RatingMode"];
type RatingPeriod = components["schemas"]["RatingPeriod"];
type CommentSort = components["schemas"]["CommentSort"];

/**
 * Ключи кэша в одном месте: разъехавшийся ключ не ломает сборку, он просто
 * перестаёт инвалидироваться — и экран показывает вчерашний баланс.
 *
 * Матрица инвалидации после мутаций:
 *   buy             → home, wallet, myRobots, transactions, activity, profile
 *   stop            → home, myRobots, myRobot(id), activity
 *   deposit create  → activity (баланс не двигается: зачисляет администратор)
 *   deposit создан  → depositRequest(id) через setQueryData; дальше экран
 *                     опрашивает её сам, пока платёж не станет терминальным
 *   withdraw create → wallet, activity
 *   read-all        → activity (все фильтры)
 *   profile patch   → profile через setQueryData
 *   demo start/stop/reset → demo, demoPosition(id), demoTransactions
 *   comment create/edit/delete → robot(id) целиком: и сводка на карточке,
 *                                и все три вкладки сортировки
 *   helpful vote    → robotComments(id, sort) через setQueryData
 *   report          → ничего: жалоба не меняет того, что видит жалующийся
 *
 * Ключи демо-режима — отдельная ветка, а не префикс к существующим. Так
 * задумано: демо и реал живут в разных таблицах на бэкенде (доработки §3.2), и
 * общий ключ означал бы, что виртуальный баланс перетирает реальный в кэше
 * React Query при переключении режима. Ровно тот demo/real mix-up, от которого
 * предостерегает §10, только на клиенте.
 */
export const qk = {
  // Период входит в ключ: ряды за 24Ч и за 90Д — разные данные, и общий
  // ключ заставлял бы перерисовывать график чужими точками на каждое
  // переключение чипа.
  home: (period: ChartPeriod) => ["home", period] as const,

  // Календарное окно заработка. Отдельный тип от `ChartPeriod` и отдельный
  // ключ: «месяц» здесь означает не то же, что `30d` на графике робота.
  earnings: (period: EarningsPeriod) => ["earnings", period] as const,

  rating: (mode: RatingMode, period: RatingPeriod) => ["rating", mode, period] as const,

  robots: (market?: MarketFilter | null) => ["robots", market ?? "all"] as const,
  robot: (id: number) => ["robots", "detail", id] as const,
  // Период в ключе: переключение 1M…ALL обязано попадать в кеш, а не
  // перетирать один и тот же ряд.
  robotAnalytics: (id: number, period: ShowcasePeriod) =>
    ["robots", "detail", id, "analytics", period] as const,

  // Вложены в `robot(id)` намеренно: одна инвалидация по этому префиксу
  // обновляет и сводку на карточке, и все три вкладки сортировки. Отдельная
  // ветка потребовала бы помнить про вторую инвалидацию в каждой мутации —
  // и однажды её забыли бы, оставив счётчик отзывов расходиться с лентой.
  robotComments: (id: number, sort: CommentSort) =>
    ["robots", "detail", id, "comments", sort] as const,

  myRobots: () => ["my-robots"] as const,
  myRobot: (id: number, period: ChartPeriod) => ["my-robots", "detail", id, period] as const,

  wallet: () => ["wallet"] as const,
  transactions: (type?: TransactionType | null) => ["wallet", "transactions", type ?? "all"] as const,
  depositRequest: (id: number) => ["wallet", "deposit", id] as const,
  depositQuote: (asset: string, network: string, amount: string) =>
    ["wallet", "deposit", "quote", asset, network, amount] as const,
  withdrawalRequest: (id: number) => ["wallet", "withdraw", id] as const,

  activity: (filter: ActivityFilter) => ["activity", filter] as const,

  profile: () => ["profile"] as const,
  referral: () => ["profile", "referral"] as const,
  legal: (slug: string) => ["legal", slug] as const,

  // ── Демо-счёт (доработки §3) ──────────────────────────────
  demo: (period: ChartPeriod) => ["demo", period] as const,
  demoPosition: (id: number, period: ChartPeriod) =>
    ["demo", "positions", id, period] as const,
  demoTransactions: () => ["demo", "transactions"] as const,
} as const;

/**
 * Корень демо-ветки. Мутации демо инвалидируют его целиком, а не перечисляют
 * ключи: экранов в демо мало, а пропущенный ключ стоил бы устаревшего
 * виртуального баланса на глазах у пользователя.
 */
export const DEMO_ROOT = ["demo"] as const;
